import React, { useState, useEffect, useMemo } from 'react';
import { 
  Tabs, 
  Tab, 
  Button, 
  Form, 
  Container, 
  Modal, 
  Card, 
  Row, 
  Col, 
  Spinner,
  Badge,
  ProgressBar,
  Pagination,
  InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import { 
  FaUserTie, 
  FaStar, 
  FaEdit, 
  FaTrashAlt, 
  FaPlus, 
  FaSearch, 
  FaFilter, 
  FaCheckCircle,
  FaChartLine,
  FaAward,
  FaClock,
  FaUserCircle,
  FaImage,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const ExpertList = () => {
  const [experts, setExperts] = useState([]);
  const [filteredExperts, setFilteredExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  
  const [newExpert, setNewExpert] = useState({ 
    name: '', 
    position: '', 
    jobs_done: '', 
    rating: '', 
    image: '', 
    description: '' 
  });
  
  const [editExpert, setEditExpert] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expertToDelete, setExpertToDelete] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const expertsPerPage = 6;

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://api.collegeall.in/api/v1/experts');
      setExperts(res.data || []);
      setFilteredExperts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch experts:', err);
      toast.error('Failed to retrieve expert directory.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  // Filter Logic
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = experts.filter(expert => 
      expert.name?.toLowerCase().includes(term) || 
      expert.position?.toLowerCase().includes(term) ||
      expert.description?.toLowerCase().includes(term)
    );
    setFilteredExperts(filtered);
    setCurrentPage(1);
  }, [searchTerm, experts]);

  // Pagination Logic (Sliding Window)
  const totalPages = Math.ceil(filteredExperts.length / expertsPerPage);
  const indexOfLastExpert = currentPage * expertsPerPage;
  const indexOfFirstExpert = indexOfLastExpert - expertsPerPage;
  const currentExperts = filteredExperts.slice(indexOfFirstExpert, indexOfLastExpert);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
      return items;
    }

    let start = currentPage - 1;
    let end = currentPage + 1;

    if (start <= 1) {
      start = 1;
      end = 3;
    } else if (end >= totalPages) {
      end = totalPages;
      start = totalPages - 2;
    }

    if (start > 1) {
      items.push(<Pagination.Ellipsis key="start-el" disabled />);
    }

    for (let i = start; i <= end; i++) {
      if (i < totalPages) {
        items.push(
          <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
            {i}
          </Pagination.Item>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="end-el" disabled />);
    }

    items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
        {totalPages}
      </Pagination.Item>
    );

    return items;
  };

  const handleAddExpert = async (e) => {
    e.preventDefault();
    try {
      await axios.post('https://api.collegeall.in/api/v1/add-expert', newExpert);
      fetchExperts();
      setNewExpert({ name: '', position: '', jobs_done: '', rating: '', image: '', description: '' });
      setActiveTab('list');
      toast.success('Expert profile onboarded successfully.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create expert profile.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://api.collegeall.in/api/v1/update-expert/${editExpert.id}`, editExpert);
      setShowEditModal(false);
      fetchExperts();
      toast.success('Expert information updated successfully.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update expert information.');
    }
  };

  const handleDeleteExpert = async () => {
    try {
      await axios.delete(`https://api.collegeall.in/api/v1/delete-expert/${expertToDelete.id}`);
      fetchExperts();
      setShowDeleteModal(false);
      toast.success('Expert has been decommissioned.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to decommission expert.');
    }
  };

  const stats = useMemo(() => [
    { title: 'Verified Experts', count: experts.length, icon: <FaUserTie />, color: '#6366f1' },
    { title: 'Highly Rated', count: experts.filter(e => parseFloat(e.rating) >= 4.5).length, icon: <FaAward />, color: '#f59e0b' },
    { title: 'Top Performers', count: experts.filter(e => parseInt(e.jobs_done) > 40).length, icon: <FaChartLine />, color: '#10b981' },
    { title: 'Recently Joined', count: 4, icon: <FaClock />, color: '#ec4899' },
  ], [experts]);

  return (
    <div className="expert-mgmt-root p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Global Expert Panel</h2>
          <p className="text-muted small mb-0">Curate and manage our network of industry leading consultants</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setActiveTab('add')}
          className="btn-modern shadow-sm"
        >
          <FaPlus className="me-2" /> Onboard New Expert
        </Button>
      </div>

      {/* Metrics Row */}
      <Row className="g-3 mb-4">
        {stats.map((stat, i) => (
          <Col md={3} key={i}>
            <Card className="border-0 shadow-sm metric-card p-3">
              <div className="d-flex align-items-center">
                <div className="metric-icon" style={{ color: stat.color, background: `${stat.color}15` }}>
                  {stat.icon}
                </div>
                <div className="ms-3">
                  <div className="small text-muted fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.65rem' }}>{stat.title}</div>
                  <h4 className="fw-bold mb-0">{stat.count}</h4>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Tabs 
            activeKey={activeTab} 
            onSelect={(k) => { setActiveTab(k); }}
            className="modern-tabs border-0"
          >
            <Tab eventKey="list" title="Active Directory" />
            <Tab eventKey="add" title="Expert Onboarding" />
          </Tabs>

          {activeTab === 'list' && (
            <InputGroup className="search-group" style={{ maxWidth: '300px' }}>
              <InputGroup.Text className="bg-light border-0">
                <FaSearch className="text-muted small" />
              </InputGroup.Text>
              <Form.Control
                className="bg-light border-0 small shadow-none"
                placeholder="Search by name, role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'list' ? (
            <div className="p-4">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" variant="primary" />
                </div>
              ) : filteredExperts.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaUserCircle size={48} className="mb-3 opacity-25" />
                  <p>No experts matching your criteria were found.</p>
                </div>
              ) : (
                <>
                  <Row className="g-4">
                    {currentExperts.map((exp) => (
                      <Col key={exp.id} lg={4} md={6}>
                        <Card className="h-100 border-0 shadow-sm expert-modern-card overflow-hidden">
                          <div className="expert-card-image">
                            {exp.image ? (
                              <img src={exp.image} alt={exp.name} onError={(e) => { e.target.src='https://ui-avatars.com/api/?name='+exp.name+'&background=6366f1&color=fff' }} />
                            ) : (
                              <div className="empty-avatar"><FaUserCircle /></div>
                            )}
                            <Badge bg="white" text="dark" className="rating-pill shadow-sm">
                              <FaStar className="text-warning me-1" /> {exp.rating}
                            </Badge>
                          </div>
                          <Card.Body className="pt-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="fw-bold mb-0 text-dark">{exp.name}</h6>
                                <span className="x-small text-primary fw-bold text-uppercase">{exp.position}</span>
                              </div>
                              <div className="d-flex gap-2">
                                <button className="action-circle-btn edit" onClick={() => { setEditExpert(exp); setShowEditModal(true); }}><FaEdit /></button>
                                <button className="action-circle-btn delete" onClick={() => { setExpertToDelete(exp); setShowDeleteModal(true); }}><FaTrashAlt /></button>
                              </div>
                            </div>
                            
                            <div className="stats-box mb-3 p-2 rounded-2 bg-light-subtle border">
                              <div className="d-flex justify-content-between x-small mb-1">
                                <span className="text-muted">Impact (Jobs)</span>
                                <span className="fw-bold">{exp.jobs_done}</span>
                              </div>
                              <ProgressBar now={Math.min(100, (exp.jobs_done / 50) * 100)} variant="success" style={{ height: '4px' }} />
                            </div>

                            <p className="card-text text-muted x-small line-clamp-2">
                              {exp.description || 'Experienced professional with a proven track record in the field.'}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {totalPages > 1 && (
                    <div className="p-3 bg-light mt-5 border rounded-3 d-flex justify-content-between align-items-center">
                      <div className="small text-muted">
                        Showing {indexOfFirstExpert + 1} to {Math.min(indexOfLastExpert, filteredExperts.length)} of {filteredExperts.length} consultants
                      </div>
                      <Pagination className="mb-0 modern-pagination">
                        <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
                        {renderPaginationItems()}
                        <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="p-4 bg-white">
              <Form onSubmit={handleAddExpert}>
                <Row className="g-4">
                  <Col md={8}>
                    <div className="section-label mb-3">Identity & Expertise</div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Full Name</Form.Label>
                          <Form.Control 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="e.g. Dr. Sarah Jenkins"
                            value={newExpert.name}
                            onChange={(e) => setNewExpert({...newExpert, name: e.target.value})}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Core Designation</Form.Label>
                          <Form.Control 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="e.g. Principal Architect"
                            value={newExpert.position}
                            onChange={(e) => setNewExpert({...newExpert, position: e.target.value})}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="x-small fw-bold text-muted text-uppercase">Expert Profile Bio</Form.Label>
                          <Form.Control 
                            as="textarea" 
                            rows={6} 
                            className="bg-light border-0 shadow-none" 
                            required 
                            placeholder="Brief professional background..."
                            value={newExpert.description}
                            onChange={(e) => setNewExpert({...newExpert, description: e.target.value})}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>

                  <Col md={4}>
                    <div className="section-label mb-3">Performance & Assets</div>
                    <div className="p-3 border rounded-3 bg-light-subtle">
                      <div className="text-center mb-3">
                        <div className="preview-avatar-lg shadow-sm mx-auto mb-2">
                           {newExpert.image ? (
                             <img src={newExpert.image} alt="Preview" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />
                           ) : null}
                           <div className="preview-placeholder d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                             <FaUserCircle size={40} />
                           </div>
                        </div>
                        <span className="x-small text-muted fw-bold">IMAGE PREVIEW</span>
                      </div>
                      <Row className="g-3">
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Jobs Done</Form.Label>
                            <Form.Control 
                              type="number"
                              className="bg-white border shadow-none" 
                              required 
                              value={newExpert.jobs_done}
                              onChange={(e) => setNewExpert({...newExpert, jobs_done: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase">Rating</Form.Label>
                            <Form.Control 
                              type="number"
                              step="0.1"
                              max="5"
                              className="bg-white border shadow-none" 
                              required 
                              value={newExpert.rating}
                              onChange={(e) => setNewExpert({...newExpert, rating: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label className="x-small fw-bold text-muted text-uppercase d-flex align-items-center gap-1">
                              <FaImage /> Avatar URL
                            </Form.Label>
                            <Form.Control 
                              className="bg-white border shadow-none" 
                              placeholder="https://images.unsplash.com/..."
                              value={newExpert.image}
                              onChange={(e) => setNewExpert({...newExpert, image: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-4 d-grid gap-2">
                      <Button variant="primary" type="submit" className="fw-bold py-2 shadow-sm">
                        <FaCheckCircle className="me-2" /> FINALIZE PROFILE
                      </Button>
                      <Button variant="outline-secondary" className="fw-bold border-0" onClick={() => setActiveTab('list')}>CANCEL</Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="p-4">
          {editExpert && (
            <Row className="g-4">
              <Col md={4} className="text-center border-end">
                <div className="section-label mb-3">Profile Assets</div>
                <div className="preview-avatar-lg shadow-sm mx-auto mb-3">
                  {editExpert.image ? (
                    <img src={editExpert.image} alt="Current" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />
                  ) : null}
                  <div className="preview-placeholder d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                    <FaUserCircle size={40} />
                  </div>
                </div>
                
                <Form.Group className="mb-3 text-start">
                  <Form.Label className="x-small fw-bold text-muted">AVATAR URL</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control 
                      className="bg-light border-0"
                      value={editExpert.image}
                      placeholder="Image URL..."
                      onChange={(e) => setEditExpert({...editExpert, image: e.target.value})}
                    />
                  </InputGroup>
                </Form.Group>

                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <Form.Label className="x-small fw-bold text-muted">RATING</Form.Label>
                    <Form.Control 
                      size="sm"
                      type="number"
                      step="0.1"
                      className="bg-light border-0"
                      value={editExpert.rating}
                      onChange={(e) => setEditExpert({...editExpert, rating: e.target.value})}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <Form.Label className="x-small fw-bold text-muted">JOBS</Form.Label>
                    <Form.Control 
                      size="sm"
                      type="number"
                      className="bg-light border-0"
                      value={editExpert.jobs_done}
                      onChange={(e) => setEditExpert({...editExpert, jobs_done: e.target.value})}
                    />
                  </div>
                </div>
              </Col>
              
              <Col md={8}>
                <div className="section-label mb-3">Public Information</div>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="x-small fw-bold text-muted">NAME</Form.Label>
                      <Form.Control 
                        className="bg-light border-0"
                        required
                        value={editExpert.name}
                        onChange={(e) => setEditExpert({...editExpert, name: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="x-small fw-bold text-muted">POSITION</Form.Label>
                      <Form.Control 
                        className="bg-light border-0"
                        required
                        value={editExpert.position}
                        onChange={(e) => setEditExpert({...editExpert, position: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="x-small fw-bold text-muted">DESCRIPTION</Form.Label>
                      <Form.Control 
                        as="textarea"
                        rows={5}
                        className="bg-light border-0"
                        required
                        value={editExpert.description}
                        onChange={(e) => setEditExpert({...editExpert, description: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 bg-light-subtle">
          <Button variant="light" onClick={() => setShowEditModal(false)} className="fw-semibold">Dismiss</Button>
          <Button variant="primary" onClick={handleEditSubmit} className="fw-bold px-4">Update Expert</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal danger">
        <Modal.Body className="p-4 text-center">
          <div className="delete-icon-wrapper mb-3">
            <FaTrashAlt />
          </div>
          <h5 className="fw-bold text-dark">Decommission Expert?</h5>
          <p className="text-muted small">Removing <strong>{expertToDelete?.name}</strong> will erase their profile and performance history from the global panel.</p>
          <div className="d-grid gap-2">
            <Button variant="danger" className="fw-bold py-2 shadow-sm" onClick={handleDeleteExpert}>CONFIRM DELETE</Button>
            <Button variant="light" onClick={() => setShowDeleteModal(false)} className="text-muted fw-semibold">Retain Profile</Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .expert-mgmt-root {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .metric-card {
          border-radius: 12px;
          border: 1px solid #e2e8f0 !important;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .btn-modern {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          font-size: 0.85rem;
        }

        .modern-tabs .nav-link {
          color: #64748b;
          border: 0;
          font-size: 0.85rem;
          font-weight: 600;
          padding: 1rem 1.5rem;
          position: relative;
        }

        .modern-tabs .nav-link.active {
          color: #6366f1;
          background: transparent;
        }

        .modern-tabs .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #6366f1;
        }

        .expert-modern-card {
          border-radius: 16px !important;
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0 !important;
        }

        .expert-modern-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1) !important;
          border-color: #cbd5e1 !important;
        }

        .expert-card-image {
          height: 160px;
          background: #f1f5f9;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .expert-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .preview-avatar-lg {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: #f1f5f9;
          overflow: hidden;
          border: 4px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .preview-avatar-lg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .rating-pill {
          position: absolute;
          bottom: 10px;
          right: 10px;
          padding: 4px 10px;
          border-radius: 50px;
          font-weight: 700;
          font-size: 0.75rem;
          border: 1px solid #e2e8f0;
        }

        .action-circle-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          transition: all 0.2s;
        }

        .action-circle-btn.edit { background: #e0e7ff; color: #4f46e5; }
        .action-circle-btn.delete { background: #fee2e2; color: #ef4444; }
        .action-circle-btn:hover { transform: scale(1.1); }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .section-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #94a3b8;
        }

        .modern-pagination .page-link {
          border: 0;
          background: transparent;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0 2px;
          border-radius: 6px;
        }

        .modern-pagination .page-item.active .page-link {
          background: #6366f1;
          color: white;
        }

        .delete-icon-wrapper {
          width: 60px;
          height: 60px;
          background: #fee2e2;
          color: #ef4444;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: 1.5rem;
        }

        .modern-modal .modal-content {
          border-radius: 1.5rem;
          border: 0;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .x-small { font-size: 0.75rem; }
      `}</style>
    </div>
  );
};

export default ExpertList;